import type { LegendListRef } from '@legendapp/list/react-native';
import { useRouter } from 'expo-router';
import { useRef } from 'react';
import { Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import Paginator from '@/components/Custom/Paginator';
import Placeholder from '@/components/Custom/Placeholder';
import ActionBar from '@/components/UI/ActionBar';
import ActionButton from '@/components/UI/ActionButton';
import PressableRow from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import List from '@/components/Unistyles/List';
import { graphql } from '@/gql/graphql';
import usePaginatedList from '@/hooks/usePaginatedList';
import OrganizationTeamActions from './OrganizationTeamActions';

const TEAM_PAGE_LIMIT = 25;

const GetOrganizationDetailTeams = graphql(/* GraphQL */ `
  query GetOrganizationDetailTeams(
    $organizationId: uuid!
    $teamLimit: Int!
    $teamOffset: Int!
  ) {
    event_by_pk(id: $organizationId) {
      id
      title
      permissions
    }
    team(
      where: { root_event_id: { _eq: $organizationId } }
      limit: $teamLimit
      offset: $teamOffset
      order_by: [{ title: asc }, { id: asc }]
    ) {
      id
      avatar_id
      home_primary_color
      title
      permissions
      team_people_aggregate {
        aggregate {
          count
        }
      }
      team_events(where: { event_id: { _eq: $organizationId } }, limit: 1) {
        event_id
        team_id
        is_manager
      }
    }
    team_aggregate(where: { root_event_id: { _eq: $organizationId } }) {
      aggregate {
        count
      }
    }
  }
`);

type OrganizationTeamRow = {
  id: string;
  title: string;
  avatarId: string | null;
  color: string | null;
  canEdit: boolean;
  isManager: boolean;
  memberCount: number;
};

export default function OrganizationDetailTeams({
  organizationId,
}: {
  organizationId: string;
}) {
  const { push } = useRouter();
  const listRef = useRef<LegendListRef>(null);
  const paginated = usePaginatedList({ limit: TEAM_PAGE_LIMIT, listRef });
  const [{ data, fetching, error }] = useQuery({
    query: GetOrganizationDetailTeams,
    variables: {
      organizationId,
      teamLimit: paginated.limit,
      teamOffset: paginated.offset,
    },
    requestPolicy: 'cache-and-network',
    pause: !organizationId,
  });

  const organization = data?.event_by_pk;
  const canCreateTeam = Boolean(organization?.permissions?.edit);
  const canManageTeams = Boolean(organization?.permissions?.delegate);
  const rows: OrganizationTeamRow[] = (data?.team ?? []).map((team) => ({
    id: team.id,
    title: team.title ?? 'Team',
    avatarId: team.avatar_id,
    color: team.home_primary_color,
    canEdit: Boolean(team.permissions?.edit || canManageTeams),
    isManager: team.team_events[0]?.is_manager ?? false,
    memberCount: team.team_people_aggregate.aggregate?.count ?? 0,
  }));

  return (
    <Box flex>
      {canCreateTeam ? (
        <ActionBar>
          <ActionButton
            forceLabel
            label="Create Team"
            icon="add-outline"
            variant="success"
            onPress={() =>
              push({
                pathname: '/team/create',
                params: {
                  rootEventId: organizationId,
                  rootEventTitle: organization?.title ?? '',
                },
              })
            }
          />
        </ActionBar>
      ) : null}
      {error && (
        <Box px="lg" py="md">
          <Text role="alert" variant="error">
            Could not load teams. Please try again.
          </Text>
        </Box>
      )}
      {(rows.length > 0 || !error) && (
        <Box flex testID="OrganizationTeamsList">
          <List
            ref={listRef}
            data={rows}
            fetching={fetching}
            extraData={fetching}
            keyExtractor={(team) => team.id}
            keyboardScroll={false}
            contentInsetAdjustmentBehavior={
              Platform.OS === 'ios' ? 'automatic' : undefined
            }
            contentContainerStyle={styles.content}
            ListEmptyComponent={
              <Placeholder icon="people-outline" text="No teams to show" />
            }
            ListFooterComponent={
              (data?.team_aggregate.aggregate?.count ?? 0) > paginated.limit ? (
                <Paginator
                  {...paginated}
                  total={data?.team_aggregate.aggregate?.count ?? 0}
                />
              ) : null
            }
            renderItem={({ item: team }) => (
              <PressableRow
                role={Platform.OS === 'web' ? undefined : 'button'}
                testID={`OrganizationTeamsRow-${team.id}`}
                style={styles.row}
                onPress={() =>
                  push({
                    pathname: '/organization/team/[teamId]',
                    params: { teamId: team.id },
                  })
                }
              >
                <Box
                  style={styles.identity}
                  testID={`OrganizationTeamsIdentity-${team.id}`}
                >
                  <Avatar
                    size="md"
                    id={team.avatarId ?? ''}
                    color={team.color ?? ''}
                    name={team.title}
                  />
                  <Box flex minWidth={0} space="xs" style={styles.description}>
                    <Text
                      size="md"
                      bold
                      testID={`OrganizationTeamsName-${team.id}`}
                    >
                      {team.title}
                    </Text>
                    <Box space="xs">
                      <Text
                        size="sm"
                        variant="typographySecondary"
                        testID={`OrganizationTeamsMembers-${team.id}`}
                      >
                        {team.memberCount}{' '}
                        {team.memberCount === 1 ? 'member' : 'members'}
                      </Text>
                      {team.isManager && (
                        <Text size="sm" variant="typographySecondary">
                          Management Team
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>
                {team.canEdit && (
                  <Box alignSelf="center">
                    <OrganizationTeamActions
                      teamId={team.id}
                      title={team.title}
                      disabled={fetching}
                    />
                  </Box>
                )}
              </PressableRow>
            )}
          />
        </Box>
      )}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  content: { paddingBottom: theme.space('2xl') },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    paddingHorizontal: theme.space('lg'),
    paddingVertical: 0,
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  identity: {
    flex: 1,
    flexDirection: 'row',
    minWidth: 0,
    alignItems: 'center',
    paddingVertical: theme.space('lg'),
    gap: theme.space('lg'),
  },
  description: { minHeight: theme.size(10), justifyContent: 'center' },
}));
